import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { TitleStrategy } from '@angular/router';
import { canActivate, redirectUnauthorizedTo, redirectLoggedInTo } from '@angular/fire/auth-guard';
import { RoleGuard } from './guards/role.guard';
import { Home } from './screens/home/home.component';
import { DcTitleStrategy } from './services/title.strategy';
import { DcShellComponent } from './components/dc-shell/dc-shell.component';

const redirectUnauthorized = () => redirectUnauthorizedTo(['dang-nhap']);
const redirectLoggedIn    = () => redirectLoggedInTo(['']);

const routes: Routes = [
  // ── Auth pages — outside shell ──────────────────────────────
  {
    path: 'dang-nhap',
    title: 'Đăng nhập',
    loadComponent: () => import('./components/login/login.component').then(m => m.LoginComponent),
    ...canActivate(redirectLoggedIn),
  },
  { path: 'login', redirectTo: 'dang-nhap', pathMatch: 'full' },
  {
    path: 'unauthorized',
    title: 'Không có quyền',
    loadComponent: () => import('./components/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
  },

  // ── App shell — wraps all main screens ───────────────────────
  {
    path: '',
    component: DcShellComponent,
    ...canActivate(redirectUnauthorized),
    children: [
      // Trang chủ
      { path: '', title: 'Trang chủ', component: Home, pathMatch: 'full' },
      { path: 'home', redirectTo: '', pathMatch: 'full' },
      { path: 'trang-chu', redirectTo: '', pathMatch: 'full' },

      // Thanh toán
      { path: 'payment-cancel', redirectTo: 'payment-center', pathMatch: 'full' },

      // Nhà thuốc tổng
      {
        path: 'nhaThuocTong',
        title: 'Nhà thuốc',
        loadComponent: () => import('./screens/nhaThuocTong/nhaThuocTong.component').then(m => m.NhaThuocTong),
      },

      // Phòng xét nghiệm — role: labhouse
      {
        path: 'labhouse',
        title: 'Phòng xét nghiệm',
        loadComponent: () => import('./screens/labhouse/labhouse.component').then(m => m.LabhouseComponent),
        canActivate: [RoleGuard],
        data: { roles: ['labhouse'] },
      },

      // Quản lý test sức khoẻ — role: csyt
      {
        path: 'phisical-tests',
        title: 'Quản lý test sức khoẻ',
        loadComponent: () => import('./components/phisical-tests/phisical-tests.component').then(m => m.PhisicalTestsComponent),
        canActivate: [RoleGuard],
        data: { roles: ['csyt'] },
      },

      // Quản lý hồ sơ — danh sách
      {
        path: 'quanLyHoSo',
        title: 'Danh sách hồ sơ',
        loadComponent: () => import('./screens/quanLyHoSo/ho-so-list.component').then(m => m.HoSoListComponent),
      },
      // Quản lý hồ sơ — thêm mới / cập nhật
      {
        path: 'quanLyHoSo/new',
        title: 'Hồ sơ bệnh nhân',
        loadComponent: () => import('./screens/quanLyHoSo/quanLyHoSo.component').then(m => m.QuanLyHoSo),
      },

      // Simulation — role: csyt
      {
        path: 'simulation/:documentId',
        title: 'Mô phỏng lâm sàng',
        loadComponent: () => import('./components/simulation/simulation.component').then(m => m.SimulationComponent),
        canActivate: [RoleGuard],
        data: { roles: ['csyt'] },
      },

      // Khách mua đơn thuốc
      { path: 'khachMuaDonThuoc', redirectTo: 'quanLyHoSo', pathMatch: 'full' },

      // Quản lý thành viên
      {
        path: 'quanLyThanhVien',
        title: 'Quản lý thành viên',
        loadComponent: () => import('./screens/quanLyThanhVien/quanLyThanhVien.component').then(m => m.QuanLyThanhVien),
      },

      // Báo cáo thống kê hồ sơ
      {
        path: 'bao-cao',
        title: 'Báo cáo thống kê',
        loadComponent: () => import('./components/reports/reports.component').then(m => m.ReportsComponent),
      },

      // Báo cáo tương tác sự kiện
      {
        path: 'interaction-report',
        title: 'Báo cáo sự kiện',
        loadComponent: () => import('./screens/interaction-report/interaction-report.component').then(m => m.InteractionReportComponent),
      },

      // Audit — role: auditer
      {
        path: 'audit/:id',
        title: 'Kiểm toán',
        loadComponent: () => import('./components/audit/audit.component').then(m => m.AuditComponent),
        canActivate: [RoleGuard],
        data: { roles: ['auditer'] },
      },

      // 404 — inside shell so layout stays consistent
      {
        path: '**',
        title: 'Trang không tìm thấy',
        loadComponent: () => import('./components/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
  providers: [
    { provide: TitleStrategy, useClass: DcTitleStrategy },
  ],
})
export class AppRoutingModule {}
